import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getChainConfig } from '../config/chainConfig';
import { useBlockchainService } from '../contexts/BlockchainServiceContext';

// Como Promise.allSettled(items.map(fn)) pero con como mucho `limit` peticiones
// en vuelo, para no lanzar una por productor de golpe (provocaba 429).
const mapSettledLimited = async (items, limit, fn) => {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      try {
        results[i] = { status: 'fulfilled', value: await fn(items[i]) };
      } catch (reason) {
        results[i] = { status: 'rejected', reason };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

// No gastar peticiones en pestañas ocultas
const whenVisible = (fn) => () => {
  if (typeof document !== 'undefined' && document.hidden) return;
  fn();
};

/**
 * Hook personalizado para manejar todos los datos necesarios para la página Monitor
 * @param {string} chainId - ID de la cadena de blockchain
 * @returns {Object} - Datos y estados para el componente Monitor
 */
export const useMonitorData = (chainId) => {
  // Usar el contexto de servicios de blockchain
  const { getService, getCachedData, setCachedData, hasCachedData } = useBlockchainService();
  
  // Estados
  const [producers, setProducers] = useState([]);
  const [currentProducer, setCurrentProducer] = useState('');
  const [blockchainInfo, setBlockchainInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPrice, setCurrentPrice] = useState({ price: 0, marketcap: 0, volume: 0 });
  const [apiStatus, setApiStatus] = useState({ status: 'initializing', message: 'Initializing...' });
  const [retryCount, setRetryCount] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Referencias para limpiar intervalos
  const intervalsRef = useRef({});
  // Último LIB real, en ref para que fetchBlockchainInfo no cambie de identidad en cada poll
  const lastLibRef = useRef(null);
  
  // Obtener servicio persistente
  const blockchainService = useMemo(() => {
    return chainId ? getService(chainId) : null;
  }, [chainId, getService]);
  
  // Memoizar configuración de la cadena
  const chainConfig = useMemo(
    () => chainId ? getChainConfig(chainId) : null,
    [chainId]
  );

  // Claves de cache
  const cacheKeys = useMemo(() => ({
    producers: `${chainId}-producers`,
    blockchainInfo: `${chainId}-blockchain-info`,
    price: `${chainId}-price`
  }), [chainId]);

  // Función para limpiar todos los intervalos
  const clearAllIntervals = useCallback(() => {
    Object.values(intervalsRef.current).forEach(intervalId => {
      if (intervalId) clearInterval(intervalId);
    });
    intervalsRef.current = {};
  }, []);

  // Simplified error handling
  const handleError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    
    let errorMessage = `Error in ${context}`;
    
    if (error.isRateLimitError) {
      errorMessage = error.message;
    } else if (error.isServerError) {
      errorMessage = `${error.message} (Status: ${error.statusCode})`;
    } else if (error.isNetworkError) {
      errorMessage = error.message;
    } else {
      errorMessage = `${context}: ${error.message}`;
    }
    
    return { errorMessage };
  }, []);

  // Función para actualizar el estado de la API
  const updateApiStatus = useCallback(() => {
    if (blockchainService) {
      const status = blockchainService.getApiStatus();
      setApiStatus(status);
    }
  }, [blockchainService]);

  // Función para cargar datos desde cache o API
  const loadCachedData = useCallback(async () => {
    if (!chainId) return;

    // Cargar datos desde cache inmediatamente si están disponibles
    const cachedProducers = getCachedData(cacheKeys.producers);
    const cachedBlockchainInfo = getCachedData(cacheKeys.blockchainInfo);
    const cachedPrice = getCachedData(cacheKeys.price);

    let hasAnyCache = false;

    if (cachedProducers) {
      setProducers(cachedProducers);
      hasAnyCache = true;
    }

    if (cachedBlockchainInfo) {
      setBlockchainInfo(cachedBlockchainInfo);
      setCurrentProducer(cachedBlockchainInfo.head_block_producer);
      hasAnyCache = true;
    }

    if (cachedPrice) {
      setCurrentPrice(cachedPrice);
      hasAnyCache = true;
    }

    // Si tenemos datos en cache, no mostrar loading
    if (hasAnyCache) {
      setLoading(false);
      setError(null);
    }

    return hasAnyCache;
  }, [chainId, getCachedData, cacheKeys]);

  // Función para obtener precio con cache
  const fetchPrice = useCallback(async (forceRefresh = false) => {
    if (!blockchainService) return;

    if (!forceRefresh && hasCachedData(cacheKeys.price)) {
      return; // Ya tenemos datos frescos
    }

    try {
      const response = await blockchainService.fetchFromChain('/current_price');
      setCurrentPrice(response);
      setCachedData(cacheKeys.price, response, 60000); // Cache por 1 minuto
    } catch (err) {
      const { errorMessage } = handleError(err, 'fetch price');
      console.warn(`Price fetch failed: ${errorMessage}`);
    }
  }, [blockchainService, hasCachedData, setCachedData, cacheKeys.price, handleError]);

  // Simplified producer fetching - let backend handle rate limiting
  const fetchProducers = useCallback(async (forceRefresh = false) => {
    if (!blockchainService) return;

    if (!forceRefresh && hasCachedData(cacheKeys.producers)) {
      setIsUpdating(true);
    }

    try {
      updateApiStatus();
      
      const response = await blockchainService.fetchFromChain('/get_table_rows', {
        scope: 'eosio',
        code: 'eosio',
        table: 'producers',
        json: true,
        limit: 1000
      });

      const activeProducers = response.rows.filter(producer => producer.is_active);
      const isTelos = blockchainService.getChainFamily() === 'TELOS';

      // Fetch producer info for all producers - backend will handle rate limiting
      const enrichedProducers = await mapSettledLimited(
        activeProducers, 6, async producer => {
          try {
            const producerInfo = await blockchainService.fetchFromChain(
              `/bp_data?producerName=${encodeURIComponent(producer.owner)}`
            );
            
            // Para Telos, obtener información de autovoto
            let selfVoteBoosted = 0;
            if (isTelos) {
              try {
                const voterInfo = await blockchainService.fetchFromChain(
                  `/get_voter_info/${encodeURIComponent(producer.owner)}`
                );
                selfVoteBoosted = voterInfo.self_vote_boosted || 0;
              } catch (voterError) {
                // Si no se puede obtener info de votante, asumir 0
                console.debug(`No voter info for ${producer.owner}`);
              }
            }
            
            return { ...producer, producerInfo, selfVoteBoosted };
          } catch (error) {
            const { errorMessage } = handleError(error, `producer info ${producer.owner}`);
            console.warn(errorMessage);
            return { ...producer, producerInfo: null, selfVoteBoosted: 0 };
          }
        }
      );

      const validProducers = enrichedProducers
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);

      const sortedProducers = validProducers.sort((a, b) => parseFloat(b.total_votes) - parseFloat(a.total_votes));

      // El schedule y la rotación los gestiona useProducerSchedule
      setProducers(sortedProducers);
      setCachedData(cacheKeys.producers, sortedProducers, 300000); // Cache for 5 minutes
      setError(null);
      setRetryCount(0);
      setIsUpdating(false);
      
    } catch (err) {
      const { errorMessage } = handleError(err, 'fetch producers');
      setError(errorMessage);
      setIsUpdating(false);
      setRetryCount(prev => prev + 1);
    }
  }, [blockchainService, hasCachedData, setCachedData, cacheKeys.producers, handleError, updateApiStatus]);

  // Función para obtener info de blockchain con cache
  const fetchBlockchainInfo = useCallback(async (forceRefresh = false) => {
    if (!blockchainService) return;

    if (!forceRefresh && hasCachedData(cacheKeys.blockchainInfo)) {
      return; // Ya tenemos datos frescos
    }

    try {
      const info = await blockchainService.getInfo();
      
      // Actualizar currentProducer con datos reales
      setCurrentProducer(info.head_block_producer);
      
      // Actualizar datos reales
      const realBlockInfo = {
        ...info,
        // Hora local de recepción: con ella se extrapola el head entre consultas (utils/chainClock)
        fetchedAt: Date.now(),
        libChange: lastLibRef.current != null ? info.last_irreversible_block_num - lastLibRef.current : 0
      };
      lastLibRef.current = info.last_irreversible_block_num;
      
      setBlockchainInfo(realBlockInfo);
      
      setCachedData(cacheKeys.blockchainInfo, realBlockInfo, 5000); // Cache por 5 segundos
      
    } catch (err) {
      const { errorMessage } = handleError(err, 'fetch blockchain info');
      console.warn(`Blockchain info fetch failed: ${errorMessage}`);
    }
  }, [blockchainService, hasCachedData, setCachedData, cacheKeys.blockchainInfo, handleError]);

  // Efecto principal para cargar datos
  useEffect(() => {
    if (!blockchainService || !chainId) return;

    let isMounted = true;

    const initializeData = async () => {
      // Primero cargar desde cache
      const hasCache = await loadCachedData();
      
      if (!hasCache) {
        setLoading(true);
      }

      // Luego actualizar desde API si es necesario
      await Promise.allSettled([
        fetchProducers(!hasCache),
        fetchBlockchainInfo(!hasCache),
        fetchPrice(!hasCache)
      ]);

      if (isMounted) {
        setLoading(false);
      }
    };

    // Reset estados al cambiar de blockchain
    setError(null);
    setRetryCount(0);
    setApiStatus({ status: 'initializing', message: 'Initializing...' });
    lastLibRef.current = null;

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [blockchainService, chainId, loadCachedData, fetchProducers, fetchBlockchainInfo, fetchPrice]);

  // Efecto para intervalos de actualización
  useEffect(() => {
    if (!blockchainService) return;

    // Actualizar estado de API
    updateApiStatus();
    intervalsRef.current.apiStatus = setInterval(updateApiStatus, 5000);

    // Intervalos de actualización de datos
    intervalsRef.current.producers = setInterval(whenVisible(() => fetchProducers(false)), 120000); // 2 minutos
    intervalsRef.current.blockchainInfo = setInterval(whenVisible(() => fetchBlockchainInfo(false)), 6000); // 6 segundos
    intervalsRef.current.price = setInterval(whenVisible(() => fetchPrice(false)), 60000); // 1 minuto

    return () => {
      Object.values(intervalsRef.current).forEach(intervalId => {
        if (intervalId) clearInterval(intervalId);
      });
      intervalsRef.current = {};
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockchainService, updateApiStatus, fetchProducers, fetchBlockchainInfo, fetchPrice]);

  // Función para reintentar manualmente (fuerza refetch de todas las fuentes)
  const retryFetch = useCallback(() => {
    if (!blockchainService) return;
    setError(null);
    setRetryCount(0);
    setLoading(true);

    Promise.allSettled([
      fetchProducers(true),
      fetchBlockchainInfo(true),
      fetchPrice(true)
    ]).finally(() => setLoading(false));
  }, [blockchainService, fetchProducers, fetchBlockchainInfo, fetchPrice]);

  // Limpiar intervalos al desmontar
  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, [clearAllIntervals]);

  // Retornar todos los datos y estados necesarios
  return {
    producers,
    currentProducer,
    blockchainInfo, // real + fetchedAt; el head entre consultas se calcula con estimateHead
    loading,
    error,
    currentPrice,
    chainConfig,
    apiStatus,
    retryCount,
    retryFetch,
    isUpdating
  };
};

export default useMonitorData;
